"use client";
import { Suspense, useState } from "react";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Check, MessageCircle } from "lucide-react";
import { useLocale } from "@/content/i18n/context";
import { t } from "@/content/i18n/messages";
import MarketingConsent from "@/components/forms/MarketingConsent";
import { appendMarketingConsent } from "@/lib/marketing-consent";
import { getLocalizedPathname } from "@/lib/i18n";
import { StructuredData } from "@/components/common";
import { whatsappHref } from "@/lib/whatsapp";

// Register-interest, NOT a booking. Public dates are not open yet — the first
// intake is September 2026 — so this captures a waiting list and nothing here
// may read as a confirmed seat, a price, or a guaranteed date.
//
// Web3Forms: same access key as the other forms, DISTINCT request_type
// ("Academy Register Interest") so academy leads are filterable in the inbox
// and never blur with enterprise or DGX enquiries.

// Tier codes match the cards on /academy/ so a lead maps to a course without
// interpretation. Keep in sync with certificationCourses / vibeEngineeringRungs
// / customCommunityTracks in components-pages/Academy.tsx.
const COURSES = [
  { code: "A1", label: "Claude Certified Associate — Foundations Prep", who: "2 days · non-technical" },
  { code: "A2", label: "Claude Certified Architect — Foundations Prep", who: "2 days · technical" },
  { code: "B1", label: "Vibe Engineering — Foundation", who: "1 day · no prerequisites" },
  { code: "B2", label: "Vibe Engineering — Basic", who: "2 days · Foundation graduates" },
  { code: "B3", label: "Vibe Engineering — Advance", who: "2 days · Basic graduates" },
  { code: "C1", label: "Custom in-house class", who: "1–3 days · scoped to your workflows" },
  // D1 arrives as ?course=D1 from /academy/chatgpt-training/. Scoped per client:
  // no fixed duration is published for it (see content/chatgpt-training.ts).
  { code: "D1", label: "ChatGPT training", who: "Scoped per client · business teams" },
];

const MONTHS = [
  "September 2026",
  "October 2026",
  "November 2026",
  "December 2026",
  "Q1 2027",
  "Flexible — tell me what opens first",
];

// HRD Corp registration is compulsory at 10+ Malaysian employees and optional at
// 5-9, so "not sure" is a legitimate answer from a smaller employer rather than
// a dodge. Do not force a yes/no.
// Cohorts open PER CITY, so location is not a nice-to-have — it is the field
// that decides which cohort a registration counts toward. Arrives as
// ?location=penang from the per-location training pages.
const LOCATIONS = [
  { code: "kuala-lumpur", label: "Kuala Lumpur / Selangor" },
  { code: "penang", label: "Penang" },
  { code: "johor", label: "Johor" },
  { code: "singapore", label: "Singapore" },
  { code: "other", label: "Somewhere else — tell us below" },
];

const HRDF = [
  "Yes — we contribute to HRD Corp and want the claim filed",
  "No — we will fund it directly",
  "Not sure — please check with us",
];

// FAQPage schema + visible accordion-free list. The questions people actually
// have BEFORE joining a waiting list — am I committing, what does it cost, will
// it be claimable — answered on the page rather than in a reply email.
const registerFaqs = [
  {
    question: "Am I committing to anything by joining the waiting list?",
    answer:
      "No. There is no payment and no commitment at this stage. Registering tells us which course and which month suits you, and we contact you with dates before the seats are advertised. You are free to decline when the dates land.",
  },
  {
    question: "When does the next Anchor Academy intake open?",
    answer:
      "The September 2026 intake opens soon. Dates are not yet public, which is why this is a waiting list rather than a booking — people on the list hear from us first.",
  },
  {
    question: "How much do the courses cost?",
    answer:
      "We quote on scope rather than publishing a rate card, because a two-day certification prep for six people and a custom in-house programme for forty are not the same product. The certification exam is included in the course fee. Tell us the course and headcount and we will come back with a quotation.",
  },
  {
    question: "Is the training HRD Corp claimable?",
    answer:
      "Yes, provided your company is registered with HRD Corp and paying the levy — compulsory for employers with ten or more Malaysian employees, optional at five to nine. Every course runs at least the four-hour claimable minimum, and we file the grant application and the claim for you. If you are not sure whether your company is levy-registered, choose the 'not sure' option and we will check.",
  },
  {
    question: "Can you run the course at our office?",
    answer:
      "Yes — all courses are instructor-led and delivered in your own office in Kuala Lumpur or Singapore, with labs and capstones built on your own workflows where access allows. In-house delivery usually makes sense from about six people; below that a public course is often better value and we will say so.",
  },
  {
    question: "Do you award the Claude certification?",
    answer:
      "No. We prepare candidates. Anthropic awards the certification through proctored, identity-verified exams administered via Pearson. Nobody in Malaysia awards those.",
  },
];

function RegisterInterestForm() {
  const locale = useLocale();
  const params = useSearchParams();
  const reduce = useReducedMotion();
  const inView = reduce
    ? {}
    : { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4 } };
  const lp = (href: string) => getLocalizedPathname(href, locale);

  // ?course=A1 arrives from the waiting-list buttons on the academy cards.
  const preselect = params.get("course");
  const initialCourse = COURSES.find((c) => c.code === preselect)?.code ?? "";
  const preLoc = params.get("location");
  const initialLocation = LOCATIONS.find((l) => l.code === preLoc)?.code ?? "";

  const [course, setCourse] = useState(initialCourse);
  const [location, setLocation] = useState(initialLocation);
  const [month, setMonth] = useState("");
  const [pax, setPax] = useState("1");
  const [hrdf, setHrdf] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");

  const ready = Boolean(course) && Boolean(email) && Boolean(name);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    setStatus("sending");
    const key = process.env.NEXT_PUBLIC_WEB3FORMS_KEY;
    const chosen = COURSES.find((c) => c.code === course);
    const body = new FormData();
    body.append("access_key", key ?? "");
    body.append("request_type", "Academy Register Interest");
    body.append("source", "academy-register-interest");
    body.append(
      "subject",
      `Academy waiting list — ${course} — ${location || "location TBC"} — ${pax} pax — ${company || name}`,
    );
    body.append("from_name", "Anchor Sprint Website");
    body.append("name", name);
    body.append("company", company);
    body.append("email", email);
    body.append("phone", phone);
    body.append("course_code", course);
    body.append("course", chosen ? `${chosen.code} — ${chosen.label}` : course);
    body.append("location", LOCATIONS.find((l) => l.code === location)?.label ?? location);
    body.append("preferred_month", month);
    body.append("pax", pax);
    body.append("hrdf_sponsorship", hrdf);
    body.append("notes", notes);
    appendMarketingConsent(body, marketingConsent, locale);
    try {
      const res = await fetch("https://api.web3forms.com/submit", { method: "POST", body });
      if (!res.ok) throw new Error(String(res.status));
      setStatus("sent");
      if (typeof window !== "undefined" && "gtag" in window) {
        (window as unknown as { gtag: (...a: unknown[]) => void }).gtag(
          "event",
          "academy_register_interest_submit",
          { course, location, pax, hrdf },
        );
      }
    } catch {
      setStatus("error");
    }
  };

  const label =
    "block text-xs font-semibold uppercase tracking-[0.14em] text-neutral-muted mb-2";
  const input =
    "w-full rounded-lg border border-border bg-card px-4 py-2.5 text-sm text-neutral-emphasis placeholder:text-neutral-muted/60 focus:border-primary focus:outline-none";
  const pill = (active: boolean) =>
    `inline-flex min-h-11 items-center rounded-full border px-4 py-1.5 text-sm transition ${
      active
        ? "border-primary bg-primary/10 font-medium text-primary"
        : "border-border bg-card text-neutral-muted hover:border-primary/40 hover:text-primary"
    }`;

  if (status === "sent") {
    return (
      <div className="rounded-lg border border-primary/30 bg-primary/5 p-10 text-center">
        <Check className="mx-auto h-10 w-10 text-primary" aria-hidden />
        <h2 className="mt-4 font-display text-2xl font-bold text-neutral-emphasis">
          {t(locale, "You are on the list.")}
        </h2>
        <p className="mx-auto mt-3 max-w-md text-neutral-muted">
          {t(
            locale,
            "We will email you as soon as dates open for that course — you will hear from us before the seats are advertised. If you asked us to check HRD Corp eligibility, we will come back on that too.",
          )}
        </p>
        <a
          href={whatsappHref("Hi Anchor Sprint, I just registered interest in a course and have a question (from your Academy registration page).")}
          target="_blank"
          rel="noopener noreferrer"
          data-track-event="contact_whatsapp_click"
          data-track-location="academy_register_success"
          data-track-phone="601159246128"
          className="mt-6 inline-flex items-center gap-2 rounded-full border border-primary px-6 py-2.5 text-sm font-semibold text-primary transition hover:bg-primary/5"
        >
          <MessageCircle className="h-4 w-4" aria-hidden />
          {t(locale, "WhatsApp us")}
        </a>
      </div>
    );
  }

  return (
    <motion.form {...inView} onSubmit={submit} className="space-y-8">
      <div>
        <span className={label}>{t(locale, "Which course?")}</span>
        <ul className="grid gap-2 sm:grid-cols-2">
          {COURSES.map((c) => {
            const active = course === c.code;
            return (
              <li key={c.code}>
                <button
                  type="button"
                  onClick={() => setCourse(c.code)}
                  aria-pressed={active}
                  className={`w-full rounded-lg border p-3 text-left transition ${
                    active
                      ? "border-primary bg-primary/5"
                      : "border-border bg-card hover:border-primary/40"
                  }`}
                >
                  <span className="block text-xs font-semibold uppercase tracking-wider text-neutral-muted">
                    {c.code}
                  </span>
                  <span className="mt-0.5 block text-sm font-semibold text-neutral-emphasis">
                    {t(locale, c.label)}
                  </span>
                  <span className="mt-0.5 block text-xs text-neutral-muted">
                    {t(locale, c.who)}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <div>
        <span className={label}>{t(locale, "Where are you based?")}</span>
        <div className="flex flex-wrap gap-2">
          {LOCATIONS.map((l) => (
            <button key={l.code} type="button" onClick={() => setLocation(l.code)} className={pill(location === l.code)}>
              {t(locale, l.label)}
            </button>
          ))}
        </div>
        <p className="mt-2 text-xs text-neutral-muted">
          {t(locale, "Cohorts open per city — this tells us which one your registration counts toward.")}
        </p>
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <span className={label}>{t(locale, "Which month suits you?")}</span>
          <div className="flex flex-wrap gap-2">
            {MONTHS.map((m) => (
              <button key={m} type="button" onClick={() => setMonth(m)} className={pill(month === m)}>
                {t(locale, m)}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label htmlFor="ri-pax" className={label}>
            {t(locale, "How many people?")}
          </label>
          <input
            id="ri-pax"
            type="number"
            min={1}
            inputMode="numeric"
            value={pax}
            onChange={(e) => setPax(e.target.value)}
            className={input}
          />
          <p className="mt-2 text-xs text-neutral-muted">
            {t(locale, "In-house delivery usually makes sense from about six people.")}
          </p>
        </div>
      </div>

      <div>
        <span className={label}>{t(locale, "HRD Corp sponsorship")}</span>
        <div className="flex flex-wrap gap-2">
          {HRDF.map((h) => (
            <button key={h} type="button" onClick={() => setHrdf(h)} className={pill(hrdf === h)}>
              {t(locale, h)}
            </button>
          ))}
        </div>
        <p className="mt-2 text-xs text-neutral-muted">
          {t(
            locale,
            "To claim, your company must be registered with HRD Corp and paying the levy — compulsory at ten or more Malaysian employees, optional at five to nine. We file the grant application and the claim for you.",
          )}
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="ri-name" className={label}>{t(locale, "Your name")}</label>
          <input id="ri-name" required value={name} onChange={(e) => setName(e.target.value)} className={input} />
        </div>
        <div>
          <label htmlFor="ri-company" className={label}>{t(locale, "Company")}</label>
          <input id="ri-company" value={company} onChange={(e) => setCompany(e.target.value)} className={input} />
        </div>
        <div>
          <label htmlFor="ri-email" className={label}>{t(locale, "Work email")}</label>
          <input id="ri-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={input} />
        </div>
        <div>
          <label htmlFor="ri-phone" className={label}>{t(locale, "Phone (WhatsApp optional)")}</label>
          <input id="ri-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className={input} />
        </div>
      </div>

      <div>
        <label htmlFor="ri-notes" className={label}>
          {t(locale, "Anything else — team background, workflows you want covered, preferred location")}
        </label>
        <textarea id="ri-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className={input} />
      </div>

      <MarketingConsent
        id="ri-marketing-consent"
        checked={marketingConsent}
        onChange={setMarketingConsent}
        locale={locale}
      />

      <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
        <button
          type="submit"
          disabled={status === "sending" || !ready}
          className="inline-flex items-center gap-2 rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis disabled:cursor-not-allowed disabled:opacity-50"
        >
          {status === "sending" ? t(locale, "Sending…") : t(locale, "Join the waiting list")}
          <ArrowRight className="h-4 w-4" aria-hidden />
        </button>
        <Link href={lp("/academy/")} className="text-sm font-medium text-primary hover:underline">
          {t(locale, "Back to the course list")}
        </Link>
      </div>
      {status === "error" && (
        <p className="text-sm text-destructive">
          {t(locale, "Something went wrong sending the form — please try again, or WhatsApp us instead.")}
        </p>
      )}
    </motion.form>
  );
}

export default function AcademyRegisterInterest() {
  const locale = useLocale();
  return (
    <main className="bg-background">
      <section className="pb-16 pt-24 lg:pt-28">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <p className="text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
            {t(locale, "Anchor Academy")}
          </p>
          <h1 className="mt-5 font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-5xl">
            {t(locale, "Register your interest")}
          </h1>
          <p className="mt-5 text-lg text-neutral-muted">
            {t(
              locale,
              "Our September intake opens soon. Tell us which course and which month suits you, and we will contact you with dates before the seats are advertised — no payment and no commitment at this stage.",
            )}
          </p>
          {/*
            Server-rendered course list, deliberately OUTSIDE the Suspense
            boundary below. On a static export the boundary emits its fallback
            into the HTML, so everything inside <Suspense> is invisible to
            crawlers and answer engines — the same failure that once left the
            blog index with zero links. Without this block the page ships with
            nothing but an h1. It also means a visitor sees the courses before
            any JavaScript runs.
          */}
          <section className="mt-12" aria-labelledby="courses-open">
            <h2
              id="courses-open"
              className="font-display text-xl font-semibold text-neutral-emphasis"
            >
              {t(locale, "Courses you can register for")}
            </h2>
            <ul className="mt-5 space-y-4">
              {COURSES.map((c) => (
                <li key={c.code} className="border-l-2 border-border pl-4">
                  <p className="text-sm font-semibold text-neutral-emphasis">
                    <span className="text-primary">{c.code}</span> · {t(locale, c.label)}
                  </p>
                  <p className="mt-0.5 text-sm text-neutral-muted">{t(locale, c.who)}</p>
                </li>
              ))}
            </ul>
            <p className="mt-5 text-sm text-neutral-muted">
              {t(
                locale,
                "All courses are instructor-led and delivered in your office in Kuala Lumpur or Singapore, and all are HRD Corp claimable with the grant application and claim filed by us.",
              )}
            </p>
          </section>

          <section className="mt-14" aria-labelledby="ri-faq">
            <StructuredData type="faq" faqs={registerFaqs} />
            <h2
              id="ri-faq"
              className="font-display text-xl font-semibold text-neutral-emphasis"
            >
              {t(locale, "Before you register")}
            </h2>
            <div className="mt-5 space-y-5">
              {registerFaqs.map((f) => (
                <div key={f.question} className="rounded-lg border border-border bg-card p-5">
                  <h3 className="text-sm font-semibold text-neutral-emphasis">
                    {t(locale, f.question)}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-neutral-muted">
                    {t(locale, f.answer)}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <div className="mt-14">
            {/* Static export: useSearchParams must sit inside Suspense. */}
            <Suspense fallback={null}>
              <RegisterInterestForm />
            </Suspense>
          </div>
        </div>
      </section>
    </main>
  );
}
